import type { CompanyStatus, CustomUnitKind, MembershipStatus, TireLocation, TireRemoveReason, TripStatus, Unit, UnitCategory, UnitComponent, UnitMeterKind, UnitReport, UnitReportSeverity, UnitReportStatus, UnitService, UnitServiceKind, UnitStatus, UnitDocument, DocumentStatus, MeterReading } from './types';

const dtf = new Intl.DateTimeFormat('es-MX', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
const df = new Intl.DateTimeFormat('es-MX', { day: 'numeric', month: 'short', year: 'numeric' });

export function dateTime(iso: string | null | undefined): string {
  const t = iso ? Date.parse(iso) : NaN;
  return Number.isFinite(t) ? dtf.format(t) : '—';
}

export function dateOnly(iso: string | null | undefined): string {
  const t = iso ? Date.parse(iso) : NaN;
  return Number.isFinite(t) ? df.format(t) : '—';
}

/** "hace 3 h", "hace 2 d" — para marcas de tiempo de la cola de revisión. */
export function timeAgo(iso: string | null | undefined): string {
  const t = iso ? Date.parse(iso) : NaN;
  if (!Number.isFinite(t)) return '—';
  const diff = Date.now() - t;
  const min = Math.round(diff / 60000);
  if (min < 1) return 'ahora mismo';
  if (min < 60) return `hace ${min} min`;
  const h = Math.round(min / 60);
  if (h < 24) return `hace ${h} h`;
  const d = Math.round(h / 24);
  if (d < 30) return `hace ${d} d`;
  return dateOnly(iso);
}

/** Búsqueda sin acentos ni mayúsculas: «leon» también encuentra «León». */
export function fold(value: string): string {
  return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
}

export function uptime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return '—';
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (d > 0) return `${d} d ${h} h`;
  if (h > 0) return `${h} h ${m} min`;
  return `${m} min`;
}

export const MEMBER_STATUS: Record<MembershipStatus, { label: string; tone: 'ok' | 'warn' | 'bad' }> = {
  active: { label: 'Activo', tone: 'ok' },
  pending: { label: 'Pendiente', tone: 'warn' },
  blocked: { label: 'Suspendido', tone: 'bad' },
};

export const COMPANY_STATUS: Record<CompanyStatus, { label: string; tone: 'ok' | 'warn' | 'bad' }> = {
  active: { label: 'Autorizada', tone: 'ok' },
  pending: { label: 'Por autorizar', tone: 'warn' },
  blocked: { label: 'Suspendida', tone: 'bad' },
};

export const TRIP_STATUS: Record<TripStatus, { label: string; tone: 'ok' | 'warn' | 'bad' | 'muted' | 'info' }> = {
  assigned: { label: 'Asignado', tone: 'info' },
  active: { label: 'En curso', tone: 'ok' },
  done: { label: 'Terminado', tone: 'muted' },
  cancelled: { label: 'Cancelado', tone: 'bad' },
};

export const ROLE_LABEL: Record<string, string> = { admin: 'Administrador', driver: 'Chofer', office: 'Oficina' };

// ---- Unidades y documentos ----

/** Categorías de la flota: vehículos (motriz), remolques (remolcados) y
    equipo (cuelga de un remolque). Los viajes piden vehicle+attachment. */
export const UNIT_CATEGORY: Record<UnitCategory, { label: string; plural: string }> = {
  vehicle: { label: 'Vehículo', plural: 'Vehículos' },
  attachment: { label: 'Remolque', plural: 'Remolques' },
  equipment: { label: 'Equipo', plural: 'Equipo' },
};

/** Tipos integrados: etiqueta + categoría + icono del riel de iconos. */
export const UNIT_KIND: Record<string, { label: string; icon: string; category: UnitCategory }> = {
  tractor: { label: 'Tracto', icon: 'truck', category: 'vehicle' },
  camion: { label: 'Camión / rabón', icon: 'truck', category: 'vehicle' },
  tortuga: { label: 'Tortuga', icon: 'truck', category: 'vehicle' },
  van: { label: 'Van / pickup', icon: 'truck', category: 'vehicle' },
  trailer: { label: 'Caja seca', icon: 'cube', category: 'attachment' },
  cajaRefrigerada: { label: 'Caja refrigerada', icon: 'cube', category: 'attachment' },
  plataforma: { label: 'Plataforma', icon: 'cube', category: 'attachment' },
  lowboy: { label: 'Cama baja', icon: 'cube', category: 'attachment' },
  tanque: { label: 'Tanque / pipa', icon: 'cube', category: 'attachment' },
  tolva: { label: 'Tolva', icon: 'cube', category: 'attachment' },
  chasis: { label: 'Chasis portacontenedores', icon: 'cube', category: 'attachment' },
  reefer: { label: 'Reefer independiente', icon: 'cube', category: 'equipment' },
  equipo: { label: 'Equipo', icon: 'cube', category: 'equipment' },
};

/** Etiqueta del tipo: integrado, personalizado de la empresa o el id crudo. */
export function unitKindLabel(unit: { kind: string; category?: UnitCategory | null }, company?: { customUnitKinds?: CustomUnitKind[] } | null): string {
  return UNIT_KIND[unit.kind]?.label
    ?? company?.customUnitKinds?.find(k => k.id === unit.kind)?.label
    ?? unit.kind;
}

/** Categoría efectiva: la fijada al guardar gana; falta la deriva del tipo. */
export function unitCategoryOf(unit: { kind: string; category?: UnitCategory | null }, company?: { customUnitKinds?: CustomUnitKind[] } | null): UnitCategory {
  return unit.category
    ?? UNIT_KIND[unit.kind]?.category
    ?? company?.customUnitKinds?.find(k => k.id === unit.kind)?.category
    ?? 'equipment';
}

/** Orden de los grupos en la vista (vehículos primero) y orden de tipos dentro. */
export const UNIT_CATEGORY_ORDER: UnitCategory[] = ['vehicle', 'attachment', 'equipment'];

export const UNIT_STATUS: Record<UnitStatus, { label: string; tone: 'ok' | 'warn' | 'bad' | 'muted' }> = {
  active: { label: 'Activa', tone: 'ok' },
  shop: { label: 'En taller', tone: 'warn' },
  down: { label: 'Fuera de servicio', tone: 'bad' },
  sold: { label: 'Vendida', tone: 'muted' },
};

export const UNIT_METER: Record<UnitMeterKind, string> = { km: 'Kilómetros', mi: 'Millas', hours: 'Horas de motor', none: 'Sin contador' };
/** Unidad del contador para abreviar lecturas (km / mi / h). */
export const METER_UNIT: Record<UnitMeterKind, string> = { km: 'km', mi: 'mi', hours: 'h', none: '' };

export const KM_PER_MI = 1.60934;
/** Convierte una lectura entre km y mi (un odómetro US puede marcar millas
    aunque la ficha lleve km). Misma unidad → el valor idéntico; al cruzar se
    redondea a entero (los contadores se guardan enteros). */
export function convertMeter(value: number, from: 'km' | 'mi', to: 'km' | 'mi'): number {
  if (from === to) return value;
  return Math.round(to === 'km' ? value * KM_PER_MI : value / KM_PER_MI);
}

// Reportes de falla de la unidad (companies/<cid>/unitReports).
export const REPORT_SEVERITY: Record<UnitReportSeverity, { label: string; tone: 'muted' | 'warn' | 'bad' }> = {
  low: { label: 'Observación', tone: 'muted' },
  medium: { label: 'Necesita servicio', tone: 'warn' },
  high: { label: 'Urgente', tone: 'bad' },
};
export const REPORT_STATUS: Record<UnitReportStatus, { label: string; tone: 'ok' | 'warn' | 'bad' | 'muted' }> = {
  open: { label: 'Abierto', tone: 'warn' },
  inShop: { label: 'En taller', tone: 'muted' },
  resolved: { label: 'Resuelto', tone: 'ok' },
};

export const UNIT_OWNER: Record<Unit['owner'], string> = { company: 'Propia', client: 'Del cliente', third: 'De tercero' };

export const DOC_STATUS: Record<DocumentStatus, { label: string; tone: 'ok' | 'warn' | 'bad' }> = {
  pendingReview: { label: 'Por verificar', tone: 'warn' },
  verified: { label: 'Verificado', tone: 'ok' },
  rejected: { label: 'Rechazado', tone: 'bad' },
};

export const METER_SOURCE: Record<MeterReading['source'], string> = {
  tripClose: 'Cierre de viaje', office: 'Oficina', driver: 'Chofer',
};

/** Nombre corto de la unidad para listas y selectores: «Tracto R-01». */
export function unitName(unit: Unit, company?: { customUnitKinds?: CustomUnitKind[] } | null): string {
  return `${unitKindLabel(unit, company)}${unit.internalNumber ? ` ${unit.internalNumber}` : ''}`;
}

/** Placas de la unidad como texto: «MX 123-ABC · US 9-8765». */
export function platesText(unit: Unit): string {
  return unit.plates.map(p => `${p.country} ${p.number}`).filter(s => s.trim().length > 2).join(' · ');
}

/** Días enteros hasta la fecha (negativo = ya venció); null si no hay fecha válida. */
export function daysUntil(iso: string | null | undefined): number | null {
  const t = iso ? Date.parse(iso) : NaN;
  if (!Number.isFinite(t)) return null;
  return Math.ceil((t - Date.now()) / 86400000);
}

/** Avisos del calendario de vencimiento (default del plan: [30,15,7] días — aquí basta el tope 30). */
export const DOC_WARN_DAYS = 30;

/** Badge de vencimiento de un documento: solo avisa dentro de la ventana o ya vencido. */
export function expiryBadge(expiresAt: string | null | undefined): { label: string; tone: 'warn' | 'bad' } | null {
  const days = daysUntil(expiresAt);
  if (days === null) return null;
  if (days < 0) return { label: `Venció hace ${Math.abs(days)} d`, tone: 'bad' };
  if (days <= DOC_WARN_DAYS) return { label: days === 0 ? 'Vence hoy' : `Vence en ${days} d`, tone: days <= 7 ? 'bad' : 'warn' };
  return null;
}

/**
 * Próximo servicio: piezas de texto por intervalo configurado — por contador
 * (km/h restantes contra último servicio) y por días (último servicio +
 * intervalo). Sirve a la unidad y a sus componentes (el reefer lleva su
 * propio contador e intervalos). `overdue` prende el rojo cuando ya venció.
 */
export function serviceDue(subject: Pick<Unit, 'meterKind' | 'meter' | 'maintenanceMeterInterval' | 'maintenanceDaysInterval' | 'lastServiceMeter' | 'lastServiceAt'> | UnitComponent): { text: string; overdue: boolean }[] {
  const out: { text: string; overdue: boolean }[] = [];
  const unitSuffix = METER_UNIT[subject.meterKind] || 'km';
  if (subject.maintenanceMeterInterval && subject.lastServiceMeter != null && subject.meter) {
    const remaining = subject.lastServiceMeter + subject.maintenanceMeterInterval - subject.meter.value;
    out.push(remaining < 0
      ? { text: `Servicio vencido hace ${Math.abs(remaining).toLocaleString('es-MX')} ${unitSuffix}`, overdue: true }
      : { text: `Quedan ${remaining.toLocaleString('es-MX')} ${unitSuffix}`, overdue: false });
  }
  if (subject.maintenanceDaysInterval && subject.lastServiceAt) {
    const due = Date.parse(subject.lastServiceAt) + subject.maintenanceDaysInterval * 86400000;
    const days = Math.ceil((due - Date.now()) / 86400000);
    out.push(days < 0
      ? { text: `Servicio vencido hace ${Math.abs(days)} d`, overdue: true }
      : { text: `Servicio ${dateOnly(new Date(due).toISOString())} (${days} d)`, overdue: false });
  }
  return out;
}

export const SERVICE_KIND: Record<UnitServiceKind, string> = {
  oil: 'Cambio de aceite', filters: 'Filtros', brakes: 'Frenos', tires: 'Llantas',
  electrical: 'Eléctrico', suspension: 'Suspensión', reefer: 'Servicio de reefer',
  inspection: 'Inspección', other: 'Servicio',
};

// Umbral de aviso «próximo» para servicios por contador: km/mi en 1,000 y
// horas en 25 — una quincena de uso típico antes de la cita.
const NEXT_METER_WARN = { km: 1000, mi: 1000, hours: 25, none: 0 } as const;

/**
 * Siguiente servicio por tipo, derivado del historial de servicios: para cada
 * (componente + tipo) el registro más reciente que fije nextMeter o nextAt.
 * Devuelve piezas {label, text, overdue} para Pendientes y los avisos.
 */
export function serviceDuesFrom(unit: Unit, services: UnitService[]): { label: string; text: string; overdue: boolean }[] {
  const componentName = (id: string | null) => {
    if (!id) return '';
    const c = (unit.components ?? []).find(x => x.id === id);
    return c ? (c.label || 'Componente') : '';
  };
  const meterOf = (componentId: string | null) => componentId
    ? (unit.components ?? []).find(c => c.id === componentId)?.meter ?? null
    : unit.meter;
  const kindOf = (componentId: string | null) => componentId
    ? ((unit.components ?? []).find(c => c.id === componentId)?.meterKind ?? 'km')
    : unit.meterKind;
  const latest = new Map<string, UnitService>();
  for (const s of services) {
    if (s.unitId !== unit.id || (!s.nextMeter && !s.nextAt)) continue;
    const key = `${s.componentId ?? ''}|${s.kind}`;
    if (!latest.has(key)) latest.set(key, s); // services llegan desc por `at`
  }
  const out: { label: string; text: string; overdue: boolean }[] = [];
  for (const s of latest.values()) {
    const name = SERVICE_KIND[s.kind] ?? 'Servicio';
    const cmp = componentName(s.componentId);
    const label = cmp ? `${name} · ${cmp}` : name;
    if (s.nextMeter !== null) {
      const meter = meterOf(s.componentId);
      if (meter && Number.isFinite(meter.value)) {
        const remaining = s.nextMeter - meter.value;
        const unitSuffix = METER_UNIT[kindOf(s.componentId)] || 'km';
        const warnLimit = NEXT_METER_WARN[kindOf(s.componentId)] || 1000;
        if (remaining <= 0) out.push({ label, text: `venció hace ${Math.abs(remaining).toLocaleString('es-MX')} ${unitSuffix}`, overdue: true });
        else if (remaining <= warnLimit) out.push({ label, text: `en ${remaining.toLocaleString('es-MX')} ${unitSuffix}`, overdue: false });
      }
    }
    if (s.nextAt) {
      const days = daysUntil(s.nextAt);
      if (days !== null && days < 0) out.push({ label, text: `venció hace ${Math.abs(days)} d`, overdue: true });
      else if (days !== null && days <= DOC_WARN_DAYS) out.push({ label, text: days === 0 ? 'vence hoy' : `en ${days} d`, overdue: false });
    }
  }
  return out;
}

/** Documentos que una unidad necesita para cruzar según el país donde opera
    (por sus placas): los tipos son del catálogo DOC_CATALOG de la vista. */
export const REQUIRED_UNIT_DOCS = {
  mx: ['tarjetaCirculacion', 'seguroMX', 'fisicoMecanica'],
  us: ['seguroUS', 'usRegistration', 'dotAnnual'],
} as const;

/** Estado de un tipo de documento en el checklist «para cruzar» de la unidad. */
export type DocReadiness = 'vigente' | 'porVencer' | 'vencido' | 'revision' | 'falta';
export const DOC_READINESS: Record<DocReadiness, { label: string; tone: 'ok' | 'warn' | 'bad' | 'muted' }> = {
  vigente: { label: 'Vigente', tone: 'ok' },
  porVencer: { label: 'Por vencer', tone: 'warn' },
  vencido: { label: 'Vencido', tone: 'bad' },
  revision: { label: 'Por verificar', tone: 'warn' },
  falta: { label: 'Falta', tone: 'muted' },
};

/**
 * Estado del tipo de documento para el checklist, considerando solo los
 * vigentes (!supersededBy): 'falta' si no hay uno válido (los rechazados no
 * cuentan), 'revision' si lo más reciente espera verificación, y para los
 * verificados 'vencido'/'porVencer' según la ventana de aviso o 'vigente'.
 * `docs` ya debe venir filtrado por sujeto (la unidad).
 */
export function docReadiness(docs: UnitDocument[], type: string): DocReadiness {
  const live = docs.filter(d => d.type === type && !d.supersededBy && d.status !== 'rejected');
  const verified = live.filter(d => d.status === 'verified');
  if (!verified.length) return live.some(d => d.status === 'pendingReview') ? 'revision' : 'falta';
  // El vigente con más tiempo por delante decide (normalmente hay uno por tipo).
  const best = verified.reduce((a, b) =>
    (daysUntil(a.expiresAt) ?? Number.MAX_SAFE_INTEGER) >= (daysUntil(b.expiresAt) ?? Number.MAX_SAFE_INTEGER) ? a : b);
  const badge = expiryBadge(best.expiresAt);
  if (badge?.tone === 'bad') return 'vencido';
  if (badge?.tone === 'warn') return 'porVencer';
  return 'vigente';
}

export interface UnitAlert { text: string; tone: 'warn' | 'bad' }

/**
 * Lo que pide atención de una unidad — alimenta el badge del menú Unidades y
 * la tira de avisos de la vista: estado parado, servicio vencido (intervalo
 * genérico o por tipo), documentos y reportes abiertos.
 */
export function unitAlerts(unit: Unit, docs: UnitDocument[], reports: UnitReport[], services: UnitService[] = []): UnitAlert[] {
  const out: UnitAlert[] = [];
  if (unit.status === 'shop') out.push({ text: `En taller${unit.statusNote ? ` — ${unit.statusNote}` : ''}`, tone: 'warn' });
  if (unit.status === 'down') out.push({ text: `Fuera de servicio${unit.statusNote ? ` — ${unit.statusNote}` : ''}`, tone: 'bad' });
  for (const d of serviceDue(unit)) if (d.overdue) out.push({ text: d.text, tone: 'bad' });
  for (const c of unit.components ?? [])
    for (const d of serviceDue(c)) if (d.overdue) out.push({ text: `${c.label || 'Componente'}: ${d.text}`, tone: 'bad' });
  for (const d of serviceDuesFrom(unit, services)) out.push({ text: `${d.label} ${d.text}`, tone: d.overdue ? 'bad' : 'warn' });
  const udocs = docs.filter(d => !d.supersededBy
    && d.subjectType === 'unit' && d.subjectId === unit.id);
  const { expired, expiring, rejected, pending } = docAlertCounts(udocs);
  if (expired) out.push({ text: `${expired} documento${expired === 1 ? '' : 's'} vencido${expired === 1 ? '' : 's'}`, tone: 'bad' });
  if (rejected) out.push({ text: `${rejected} documento${rejected === 1 ? '' : 's'} rechazado${rejected === 1 ? '' : 's'}`, tone: 'bad' });
  if (expiring) out.push({ text: `${expiring} documento${expiring === 1 ? '' : 's'} por vencer`, tone: 'warn' });
  if (pending) out.push({ text: `${pending} documento${pending === 1 ? '' : 's'} por revisar`, tone: 'warn' });
  const open = reports.filter(r => r.unitId === unit.id && r.status !== 'resolved');
  if (open.length) out.push({ text: `${open.length} reporte${open.length === 1 ? '' : 's'} abierto${open.length === 1 ? '' : 's'}`, tone: open.some(r => r.severity === 'high') ? 'bad' : 'warn' });
  return out;
}

/**
 * Conteo de documentos vigentes por atender, de cualquier sujeto (unidad,
 * operador o empresa). Alimenta el badge del menú Documentos — los de la
 * empresa cuentan aquí, no en el badge por unidad.
 */
export function docAlertCounts(docs: UnitDocument[]): { expired: number; expiring: number; rejected: number; pending: number } {
  const v = docs.filter(d => !d.supersededBy);
  return {
    expired: v.filter(d => expiryBadge(d.expiresAt)?.tone === 'bad').length,
    expiring: v.filter(d => expiryBadge(d.expiresAt)?.tone === 'warn').length,
    rejected: v.filter(d => d.status === 'rejected').length,
    pending: v.filter(d => d.status === 'pendingReview').length,
  };
}

// ---- Llantas (unitTires): motivos de desmontaje y ubicación del casco ----

/** Motivo de desmontaje de llanta → etiqueta (mismos códigos del contrato). */
export const TIRE_REASON: Record<TireRemoveReason, string> = {
  worn: 'Desgaste', retread: 'A reencauche', flat: 'Ponchada',
  damage: 'Daño', stolen: 'Robo', other: 'Otro',
};

/** Ubicación del casco (UnitTire.location) → etiqueta y tono del badge. */
export const TIRE_LOCATION: Record<TireLocation, { label: string; tone: 'ok' | 'warn' | 'bad' | 'info' | 'muted' }> = {
  mounted: { label: 'Montada', tone: 'ok' },
  yard: { label: 'En bodega', tone: 'info' },
  retread: { label: 'En reencauche', tone: 'warn' },
  scrap: { label: 'Desechada', tone: 'muted' },
  gone: { label: 'Robada/perdida', tone: 'bad' },
};

/** Destino por defecto al desmontar según el motivo (el usuario puede
    cambiarlo en «¿Dónde queda?»). */
export const defaultTireLocation = (reason: TireRemoveReason): TireLocation =>
  reason === 'retread' ? 'retread' : reason === 'stolen' ? 'gone' : 'yard';
